import axios from "axios";
import { store } from "../Slices";
import { setAuth } from "../Slices/AuthSlice";
import { addData } from "../Slices/DataSlice";
import { navigateTo, pathForScreen } from "./navigation";
import Swal from "sweetalert2";
import toast from "react-hot-toast";

// Function to get token from Redux store
const getToken = () => {
  const token = store.getState()?.data?.userInfo?.data;
  return token?.token || null;
};

let isUnauthorizedHandled = false; // Flag to ensure single execution

const handleUnauthorized = () => {
  if (isUnauthorizedHandled) return; // Prevent duplicate handling

  isUnauthorizedHandled = true;

  Swal.fire({
    icon: "warning",
    title: "Session Expired",
    text: "Your session has expired. Please log in again.",
    confirmButtonText: "OK",
  }).then(() => {
    // Reset the flag after the alert is closed
    isUnauthorizedHandled = false;
  });

  toast.remove();

  const dispatch = store.dispatch;
  dispatch(setAuth(false));
  dispatch(addData({ name: "practice", data: [] }));
  dispatch(addData({ name: "userInfo", data: [] }));

  // Clear per-user display data so it doesn't leak to the next user who
  // logs in on this device (these are set outside Redux in Login.jsx /
  // UserProfileBody.jsx and read in Header/Index.js).
  localStorage.removeItem("profilePicture");
  localStorage.removeItem("fullName");

  navigateTo(pathForScreen("Login"));
};

const callApi = async (
  endpoint,
  method,
  payload,
  malter = false,
  objnew = false,
  isCustomFileUpload = false,
  // { signal, timeout, onDownloadProgress } - opt-in per call, so existing
  // callers are unaffected. Needed by anything that downloads a file: without
  // a timeout axios waits forever, and without a signal a closed viewer leaves
  // the transfer running.
  options = {}
) => {
  const token = getToken();

  const authHeaders = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  let configaxios;

  if (malter) {
    const formData = new FormData();

    if (isCustomFileUpload) {
      // CUSTOM FILE UPLOAD HANDLING - For EHR checklist files
      // This handles multiple file arrays with different field names
      Object.keys(payload || {}).forEach((key) => {
        const value = payload[key];
        
        if (Array.isArray(value)) {
          // Check if this array contains files
          const hasFiles = value.some(item => 
            item instanceof File || 
            item instanceof Blob ||
            (typeof item === 'object' && item?.file instanceof File)
          );
          
          if (hasFiles) {
            // If it's an array of files, append each one with the same field name
            value.forEach((item) => {
              const file = item?.file || item;
              if (file instanceof File || file instanceof Blob) {
                formData.append(key, file);
              }
            });
          } else {
            // If it's not a file array, stringify it
            formData.append(key, JSON.stringify(value));
          }
        } else {
          // Regular field (not an array)
          formData.append(key, value?.toString() || '');
        }
      });
    } else {
      // ORIGINAL HANDLING (for backward compatibility)
      // This is for the old format where files are in payload.files array
      payload?.files?.forEach((file) => {
        formData.append("files", file);
      });
      Object.keys(payload || {}).forEach((key) => {
        if (key !== "files") {
          formData.append(key, payload[key]);
        }
      });
    }

    configaxios = {
      method,
      url: endpoint,
      data: formData,
      headers: {
        Accept: "*/*",
        "Content-Type": "multipart/form-data",
        ...authHeaders,
      },
    };
  } else {
    configaxios = {
      method,
      url: endpoint,
      data: payload,
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
        ...authHeaders,
      },
    };

    if (objnew) {
      configaxios.responseType = "blob";
    }
  }

  const { signal, timeout, onDownloadProgress } = options;
  if (signal) configaxios.signal = signal;
  if (timeout) configaxios.timeout = timeout;
  if (onDownloadProgress) configaxios.onDownloadProgress = onDownloadProgress;

  return new Promise((resolve, reject) => {
    axios(configaxios)
      .then((res) => {
        resolve(res.data);
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          handleUnauthorized();
          return;
        }
        // A deliberate abort and a timeout both arrive without a response, so
        // they would otherwise be indistinguishable from a real failure and
        // surface to the user as a generic "Request failed".
        if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
          reject({ canceled: true, message: "Request canceled" });
          return;
        }
        if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
          reject({ timedOut: true, message: "The request timed out" });
          return;
        }
        reject(error?.response?.data || { message: "Request failed" });
      });
  });
};

const callPublicApi = (endpoint, method, payload) => {
  const configaxios = {
    method,
    url: `${process.env.REACT_APP_AuthenticationService}${endpoint}`,
    data: payload,
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
  };

  return new Promise((resolve, reject) => {
    axios(configaxios)
      .then((res) => {
        if (res?.data) {
          resolve(res.data);
        } else {
          reject({ message: "No data returned from API" });
        }
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        }
        reject({
          message:
            error?.response?.data?.message ||
            "Something went wrong, please re-login",
          statusCode: error?.response?.status || 500,
        });
      });
  });
};

export { callApi, callPublicApi };
